import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface HistoryEntry {
  id: string;
  session_id: string;
  original_image_id: string;
  tool_used: string;
  settings: any;
  result_url?: string;
  result_blob?: string;
  created_at: string;
}

export const useProcessingHistory = (sessionId: string) => {
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setHistory([]);
        return;
      }

      const { data, error } = await supabase
        .from('processing_history')
        .select('*')
        .eq('session_id', sessionId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      
      setHistory(data || []);
      if (data && data.length > 0) {
        setCurrentIndex(data.length - 1);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  const addHistoryEntry = async (
    originalImageId: string,
    toolUsed: string,
    settings: any,
    resultUrl?: string,
    resultBlob?: string
  ) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('processing_history')
        .insert({
          user_id: user.id,
          session_id: sessionId,
          original_image_id: originalImageId,
          tool_used: toolUsed,
          settings,
          result_url: resultUrl,
          result_blob: resultBlob,
        })
        .select()
        .single();

      if (error) throw error;

      setHistory(prev => [...prev, data]);
      setCurrentIndex(prev => prev + 1);
      
      return data;
    } catch (error) {
      console.error('Error adding history entry:', error);
      toast({
        title: 'Error',
        description: 'Failed to save to history',
        variant: 'destructive',
      });
      return null;
    }
  };

  const undo = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
      return history[currentIndex - 1];
    }
    return null;
  }, [currentIndex, history]);

  const redo = useCallback(() => {
    if (currentIndex < history.length - 1) {
      setCurrentIndex(prev => prev + 1);
      return history[currentIndex + 1];
    }
    return null;
  }, [currentIndex, history]);

  const goToHistoryPoint = useCallback((index: number) => {
    if (index >= 0 && index < history.length) {
      setCurrentIndex(index);
      return history[index];
    }
    return null;
  }, [history]);

  const clearHistory = async () => {
    try {
      const { error } = await supabase
        .from('processing_history')
        .delete()
        .eq('session_id', sessionId);

      if (error) throw error;

      setHistory([]);
      setCurrentIndex(-1);

      toast({
        title: 'Success',
        description: 'History cleared successfully',
      });
    } catch (error) {
      console.error('Error clearing history:', error);
      toast({
        title: 'Error',
        description: 'Failed to clear history',
        variant: 'destructive',
      });
    }
  };

  const canUndo = currentIndex > 0;
  const canRedo = currentIndex < history.length - 1;
  const currentEntry = history[currentIndex] || null;

  useEffect(() => {
    if (sessionId) {
      fetchHistory();
    }
  }, [sessionId, fetchHistory]);

  return {
    history,
    currentEntry,
    currentIndex,
    loading,
    canUndo,
    canRedo,
    addHistoryEntry,
    undo,
    redo,
    goToHistoryPoint,
    clearHistory,
    refetch: fetchHistory,
  };
};